import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, roundedPoly, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

const INK = 0x1d5560; // deep-sea teal mantle
const SUCKER = 0xe89a8a; // pale sucker pads
const GLOW = 0x45d9c5;

/**
 * C7 Kraken parts — docs/02. Mantle (frame 0 normal / 1 enraged), arm bead
 * and a claw tip. Arms are chains of beads ending in a tip (kraken.ts).
 */
export const bossKrakenBody: ProceduralDef = {
  key: 'boss/kraken/body',
  width: 150,
  height: 150,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const enraged = frame === 1;

    // Mantle — bulbous dome with a slight skirt
    roundedPoly(
      ctx,
      [
        [c, c - 56],
        [c + 44, c - 32],
        [c + 52, c + 18],
        [c + 30, c + 50],
        [c - 30, c + 50],
        [c - 52, c + 18],
        [c - 44, c - 32],
      ],
      22,
    );
    fillOutlined(ctx, INK, 7);

    // Mantle ridges
    ctx.strokeStyle = css(shade(INK, 0.65));
    ctx.lineWidth = 4;
    for (const rx of [-22, 0, 22]) {
      ctx.beginPath();
      ctx.moveTo(c + rx, c - 44 + Math.abs(rx) * 0.3);
      ctx.quadraticCurveTo(c + rx * 1.2, c, c + rx, c + 42);
      ctx.stroke();
    }

    // Glow collar (matches arm beads) — red when enraged
    ctx.beginPath();
    ctx.arc(c, c + 26, 34, 0.35, Math.PI - 0.35);
    ctx.strokeStyle = css(enraged ? COLORS.dangerRed : GLOW);
    ctx.lineWidth = 6;
    ctx.stroke();

    // Eyes — heavy-lidded, blazing when enraged
    for (const side of [-1, 1] as const) {
      circle(ctx, c + side * 20, c - 8, enraged ? 11 : 9);
      fillOutlined(ctx, enraged ? COLORS.dangerRed : COLORS.dangerOrange, 3.5);
      circle(ctx, c + side * 20 + 3, c - 11, 3);
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.fill();
    }
    if (enraged) {
      circle(ctx, c, c - 8, 44);
      ctx.fillStyle = 'rgba(217, 43, 43, 0.18)';
      ctx.fill();
    }

    // Beak
    roundedPoly(ctx, [[c - 8, c + 14], [c + 8, c + 14], [c, c + 28]], 3);
    fillOutlined(ctx, shade(INK, 0.5), 3.5);
    void h;
  },
};

/** Arm bead — teal ring, dark core, one sucker pad. */
export const bossKrakenSegment: ProceduralDef = {
  key: 'boss/kraken/segment',
  width: 44,
  height: 44,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    const r = c - 5;
    circle(ctx, c, c, r);
    fillOutlined(ctx, GLOW, 4);
    circle(ctx, c, c, r * 0.6);
    ctx.fillStyle = css(INK);
    ctx.fill();
    circle(ctx, c, c, r * 0.22);
    ctx.fillStyle = css(SUCKER);
    ctx.fill();
    void h;
  },
};

/** Claw tip — pointed hook closing the arm (nose points +X). */
export const bossKrakenTip: ProceduralDef = {
  key: 'boss/kraken/tip',
  width: 52,
  height: 52,
  anchorX: 0.4,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    roundedPoly(
      ctx,
      [
        [c + 22, c],
        [c + 2, c - 14],
        [c - 16, c - 8],
        [c - 16, c + 8],
        [c + 2, c + 14],
      ],
      6,
    );
    fillOutlined(ctx, shade(INK, 1.25), 5);
    // Barb
    roundedPoly(ctx, [[c + 20, c - 2], [c + 9, c - 13], [c + 13, c - 1]], 2);
    fillOutlined(ctx, SUCKER, 3);
    circle(ctx, c - 6, c, 4);
    ctx.fillStyle = css(GLOW);
    ctx.fill();
    void h;
  },
};
